import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useDebouncedValue } from "@/hooks/useDebouncedValue"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import { useArticleList } from "../../../api/articlesApi"
import { useCustomerList } from "../../../api/customersApi"
import { useProductList } from "../../../api/productsApi"
import { useSubstanceList } from "../../../api/substancesApi"
import {
  toSupplierBucket,
  useSupplierDetail,
  useSupplierList,
} from "../../../api/suppliersApi"
import {
  productStatusLabel,
  singleSelectFacet,
  STATUS_LABEL_KEYS,
  SUPPLIER_STATUS_VALUES,
} from "../../../constants/masterDataFilters"
import { useFacetOptions } from "../../../hooks/useFacetOptions"
import {
  clearUrlParams,
  stringFilterParam,
  useArrayFilterParam,
} from "../../../hooks/useUrlFilterParams"
import { EntityListRowContent } from "../../shared/EntityListRowContent"
import { SupplierWarningBadge } from "../../shared/notes/SupplierWarningBadge"
import { RelBadge } from "../../shared/RelBadge"
import { EntityListPanel } from "../EntityListPanel"
import type { MasterDataSortValue } from "../MasterDataSort"
import { useRegisterMasterDataAgentContext } from "../masterDataAgentContext"
import { useDefaultRowSelection } from "../useDefaultRowSelection"
import { useNavSeedSearch } from "../useNavSeedSearch"

interface SupplierListPanelProps {
  selectedIds: Set<string>
  onToggleSelected: (id: string) => void
}

// F2: the dashboard tiles' `?bucket=outdated`/`?bucket=never_asked` deep
// links — no dropdown of choices (there's nothing to pick, it's a read-only
// drill-down from the tile), modeled as a facet with its own single fixed
// option so it renders through the page's existing chip mechanism (title +
// a one-click × via FacetedFilter's built-in clear affordance) exactly like
// every other filter here. Extend as more buckets link here. `awaiting` is the
// master-data tab's red gap pill (`mc_data_gaps.suppliers_awaiting`); without
// its chip the list was filtered with nothing on screen saying so.
const BUCKET_FACET_LABEL_KEYS: Partial<Record<string, TranslationKeys>> = {
  outdated: "MC_MD_SUPPLIERS_FILTER_BUCKET_OUTDATED",
  never_asked: "MC_MD_SUPPLIERS_FILTER_BUCKET_NEVER_ASKED",
  awaiting: "MC_SSD_TILE_AWAITING",
  in_review: "MC_SSD_TILE_IN_REVIEW",
}

export function SupplierListPanel({
  selectedIds,
  onToggleSelected,
}: SupplierListPanelProps) {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const notesEnabled = useFeatureFlag("MC_NOTES")
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedId = searchParams.get("id")
  useRegisterMasterDataAgentContext("supplier", selectedId)

  const [search, setSearch] = useState("")
  const debouncedSearch = useDebouncedValue(search, 300)
  const [sort, setSort] = useState<MasterDataSortValue>("name_asc")

  const [statusFilter, setStatusFilter] = useArrayFilterParam(
    searchParams,
    setSearchParams,
    "status",
  )
  const [productFilter, setProductFilter] = stringFilterParam(
    searchParams,
    setSearchParams,
    "product_id",
  )
  const [customerFilter, setCustomerFilter] = stringFilterParam(
    searchParams,
    setSearchParams,
    "customer_id",
  )
  const [articleFilter, setArticleFilter] = stringFilterParam(
    searchParams,
    setSearchParams,
    "article_id",
  )
  const [substanceFilter, setSubstanceFilter] = stringFilterParam(
    searchParams,
    setSearchParams,
    "substance_id",
  )
  const [rawBucketFilter, setBucketFilter] = stringFilterParam(
    searchParams,
    setSearchParams,
    "bucket",
  )
  // Coerce before it reaches the API: the backend 400s on an unknown bucket,
  // so a stale or hand-edited `?bucket=` must read as "no bucket filter".
  const bucketFilter = toSupplierBucket(rawBucketFilter)

  const productFacet = useFacetOptions(useProductList, (p) => ({
    value: p.id,
    label: p.name,
  }))
  const customerFacet = useFacetOptions(useCustomerList, (c) => ({
    value: c.id,
    label: c.name,
  }))
  const articleFacet = useFacetOptions(useArticleList, (a) => ({
    value: a.id,
    label: a.name,
  }))
  const substanceFacet = useFacetOptions(useSubstanceList, (s) => ({
    value: s.id,
    label: s.name,
  }))

  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
    isLoading,
  } = useSupplierList({
    q: debouncedSearch || undefined,
    product_id: productFilter ?? undefined,
    customer_id: customerFilter ?? undefined,
    article_id: articleFilter ?? undefined,
    substance_id: substanceFilter ?? undefined,
    status: statusFilter.length > 0 ? statusFilter.join(",") : undefined,
    bucket: bucketFilter ?? undefined,
    sort,
    limit: 50,
  })

  const rows = data?.pages.flatMap((p) => p.items) ?? []

  useDefaultRowSelection(rows, selectedId, setSearchParams)

  const isNavRedirect = searchParams.get("source") === "nav"
  const { data: seedDetail, isError: seedError } = useSupplierDetail(
    isNavRedirect ? selectedId : null,
  )
  useNavSeedSearch({
    isNavRedirect,
    selectedId,
    detailLoaded: !!seedDetail,
    detailErrored: seedError,
    seedSearchValue: seedDetail
      ? (seedDetail.name ?? seedDetail.supplierNumber ?? "")
      : undefined,
    setSearch,
    setSearchParams,
  })

  function selectRow(id: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set("id", id)
      return next
    })
  }

  return (
    <EntityListPanel
      agentTarget="suppliers.list"
      filterTargetKey="suppliers.filters"
      rows={rows}
      isLoading={isLoading}
      isFetching={isFetching}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      onFetchMore={fetchNextPage}
      selectedId={selectedId}
      onSelectRow={selectRow}
      selectedIds={selectedIds}
      onToggleSelected={onToggleSelected}
      totalCount={data?.pages[0]?.total}
      sort={sort}
      onSortChange={setSort}
      search={search}
      onSearchChange={setSearch}
      searchEntityLabelKey="MC_MD_TAB_SUPPLIERS"
      onClearAllFilters={() =>
        clearUrlParams(setSearchParams, [
          "product_id",
          "customer_id",
          "article_id",
          "substance_id",
          "status",
          "bucket",
        ])
      }
      facets={[
        ...(bucketFilter && BUCKET_FACET_LABEL_KEYS[bucketFilter]
          ? [
              {
                key: "bucket",
                title: translate(BUCKET_FACET_LABEL_KEYS[bucketFilter]!),
                options: [
                  {
                    value: bucketFilter,
                    label: translate(BUCKET_FACET_LABEL_KEYS[bucketFilter]!),
                  },
                ],
                ...singleSelectFacet(bucketFilter, setBucketFilter),
              },
            ]
          : []),
        {
          key: "product",
          title: translate("MC_MD_FILTER_PRODUCT_TITLE"),
          ...productFacet,
          ...singleSelectFacet(productFilter, setProductFilter),
        },
        {
          key: "customer",
          title: translate("MC_MD_FILTER_CUSTOMER_TITLE"),
          ...customerFacet,
          ...singleSelectFacet(customerFilter, setCustomerFilter),
        },
        {
          key: "article",
          title: translate("MC_MD_FILTER_ARTICLE_TITLE"),
          ...articleFacet,
          ...singleSelectFacet(articleFilter, setArticleFilter),
        },
        {
          key: "substance",
          title: translate("MC_MD_FILTER_SUBSTANCE_TITLE"),
          ...substanceFacet,
          ...singleSelectFacet(substanceFilter, setSubstanceFilter),
        },
        {
          key: "status",
          title: translate("MC_MD_FILTER_STATUS_TITLE"),
          options: SUPPLIER_STATUS_VALUES.map((value) => ({
            value,
            label: translate(STATUS_LABEL_KEYS[value]),
          })),
          selected: statusFilter,
          onSelectionChange: setStatusFilter,
        },
      ]}
      renderContent={(row) => (
        <EntityListRowContent
          title={row.name}
          badge={
            notesEnabled && row.activeWarningCount ? (
              <SupplierWarningBadge count={row.activeWarningCount} />
            ) : undefined
          }
          meta={
            <>
              <span className="font-mono">{row.supplierNumber}</span>
              {row.country && <span>{row.country}</span>}
            </>
          }
          badges={
            <>
              <RelBadge
                label={translate("MC_MD_REL_LABEL_ARTICLES")}
                labelOne={translate("MC_MD_REL_LABEL_ARTICLES_ONE")}
                count={row.articlesCount}
                tone="muted"
                items={row.relArticles.map((a) => ({
                  id: a.id,
                  primary: a.articleNumber,
                  secondary: a.name,
                }))}
                itemHref={(id) => `../articles?id=${id}&source=nav`}
                onShowAll={() => navigate(`../articles?supplier_id=${row.id}`)}
              />
              <RelBadge
                label={translate("MC_MD_REL_LABEL_PRODUCTS")}
                labelOne={translate("MC_MD_REL_LABEL_PRODUCTS_ONE")}
                count={row.productsCount}
                tone="muted"
                items={row.relProducts.map((p) => ({
                  id: p.id,
                  primary: p.name,
                  secondary: productStatusLabel(p.status, translate),
                }))}
                itemHref={(id) => `../products?id=${id}&source=nav`}
                onShowAll={() => navigate(`../products?supplier_id=${row.id}`)}
              />
              <RelBadge
                label={translate("MC_MD_REL_LABEL_SUBSTANCES")}
                labelOne={translate("MC_MD_REL_LABEL_SUBSTANCES_ONE")}
                count={row.substancesCount}
                tone="muted"
                items={row.relSubstances.map((s) => ({
                  id: s.id,
                  primary: s.name,
                }))}
                itemHref={(id) => `../substances?id=${id}&source=nav`}
                onShowAll={() =>
                  navigate(`../substances?supplier_id=${row.id}`)
                }
              />
              <RelBadge
                label={translate("MC_MD_REL_LABEL_CUSTOMERS")}
                labelOne={translate("MC_MD_REL_LABEL_CUSTOMERS_ONE")}
                count={row.customersCount}
                tone="muted"
                items={row.relCustomers.map((c) => ({
                  id: c.id,
                  primary: c.name,
                }))}
                itemHref={(id) => `../customers?id=${id}&source=nav`}
                onShowAll={() => navigate(`../customers?supplier_id=${row.id}`)}
              />
            </>
          }
        />
      )}
    />
  )
}
